import { useState } from "react";

// Mirror corteva-chat-api ADR 077.
export const USER_MEMORY_CAP = 50;
export const THREAD_MEMORY_CAP = 20;
export const MAX_MEMORY_CHARS = 500;

/** Prefix of every memory query key. Invalidating it refetches all of them. */
export const MEMORIES_QUERY_KEY = ["cc-db-api", "memories"] as const;

/** The agent tools whose results change the stored memories. */
export const MEMORY_TOOL_NAMES: readonly string[] = [
  "save_memory",
  "update_memory",
  "delete_memory",
];

const STORAGE_KEY = "enableMemory";

/** On unless the user turned it off; a missing key reads as on. */
export const isMemoryEnabled = () =>
  localStorage.getItem(STORAGE_KEY) !== "false";

export function useMemoryEnabled() {
  const [enabled, setEnabled] = useState(isMemoryEnabled);

  function setMemoryEnabled(next: boolean) {
    localStorage.setItem(STORAGE_KEY, String(next));
    setEnabled(next);
  }

  return [enabled, setMemoryEnabled] as const;
}

/**
 * Length as Postgres `char_length` counts it: code points of the trimmed text,
 * so an emoji counts once.
 */
export const memoryLength = (text: string) => [...text.trim()].length;

/**
 * Returns the message to show when a memory save fails. Uses the API's
 * `detail` when it is a string. FastAPI validation errors have a list in
 * `detail`, so for those, and for any other failure, returns a generic message.
 */
export async function memoryErrorMessage(error: unknown): Promise<string> {
  const fallback = "Couldn't save this memory";
  const response =
    error instanceof Error &&
    "response" in error &&
    error.response instanceof Response
      ? error.response
      : undefined;
  if (response?.status !== 409 && response?.status !== 422) {
    // Only 409 and 422 are rejections the user can fix. Report anything else.
    console.error("Memory save failed:", error);
  }
  if (!response) return fallback;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "detail" in body &&
      typeof body.detail === "string"
    ) {
      return body.detail;
    }
  } catch {
    // The body is not JSON. Show the generic message.
  }
  return fallback;
}
